import { Alert, theme } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { IoArrowForward, IoDownloadOutline, IoPushOutline } from 'react-icons/io5'
import { Link } from 'react-router'

import { StatusTag } from '@/Ship/Components/StatusTag'
import { classNames } from '@/Ship/Helpers/classNames'
import { dashboardManager } from '@/Ship/Stores/DashboardManager'

import type { IHomeOverview } from '@/Ship/Contracts/IHomeOverview'

import { Panel, formatInt, shortDate, shownOf, useChartColors } from './shared'
import styles from './Overview.module.css'

/** Сторінка заявок: картка відкривається за `/<сторінка>/<uuid>`. */
const APPLICATIONS_PAGE = '/applications'

export const SCHEDULE_CHIPS: ReadonlyArray<{
    key: keyof Omit<IHomeOverview['schedule'], 'unloadingOverdue'>
    title: string
}> = [
    { key: 'loadingToday', title: 'Завантажень сьогодні' },
    { key: 'loadingTomorrow', title: 'Завантажень завтра' },
    { key: 'unloadingToday', title: 'Розвантажень сьогодні' },
    { key: 'unloadingTomorrow', title: 'Розвантажень завтра' },
]

const KIND_LABELS = { loading: 'Завантаження', unloading: 'Розвантаження' } as const

/** «сьогодні», «завтра» або день тижня. */
const dayCaption = (iso: string): string => {
    const day = dayjs(iso)
    const diff = day.startOf('day').diff(dayjs().startOf('day'), 'day')

    return diff === 0 ? 'сьогодні' : diff === 1 ? 'завтра' : day.format('dd')
}

/**
 * «Рейси на тиждень»: завантаження й планові розвантаження сьогодні й
 * завтра, прострочені розвантаження (ПЛАН минув, фактичного немає) — окремим
 * попередженням, і найближчі події 7 днів із переходом у картку заявки.
 * Перелік обрізано — заголовок каже, скільки показано з усіх.
 */
export const ScheduleCard = observer(() => {
    const { token } = theme.useToken()
    const chart = useChartColors()
    const { overview, isOverviewLoading } = dashboardManager
    const schedule = overview?.schedule
    const upcoming = overview?.upcoming

    return (
        <Panel
            block="overview-schedule"
            title="Рейси на тиждень"
            subtitle="Завантаження й планові розвантаження"
            extra={upcoming ? shownOf(upcoming.items.length, upcoming.total, 'подій') : null}
            loading={isOverviewLoading && !overview}
            skeletonRows={9}
        >
            <div className={classNames(styles['chips'])}>
                {SCHEDULE_CHIPS.map((chip) => (
                    <div key={chip.key} className={classNames(styles['chip'])} data-schedule={chip.key}>
                        <div className={classNames(styles['chipValue'])}>{formatInt(schedule?.[chip.key] ?? 0)}</div>
                        <div className={classNames(styles['chipLabel'])} title={chip.title}>
                            {chip.title}
                        </div>
                    </div>
                ))}
            </div>

            {schedule && schedule.unloadingOverdue > 0 ? (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    data-testid="overview-overdue"
                    title={`Прострочене розвантаження: ${formatInt(schedule.unloadingOverdue)} — дата ПЛАН минула, а заявка ще в дорозі.`}
                />
            ) : null}

            {upcoming && upcoming.items.length > 0 ? (
                <div className={classNames(styles['events'])}>
                    {upcoming.items.map((item) => {
                        const color = item.kind === 'loading' ? chart.primary : token.colorSuccess

                        return (
                            <Link
                                key={`${item.uuid}-${item.kind}`}
                                to={`${APPLICATIONS_PAGE}/${item.uuid}`}
                                className={classNames(styles['event'])}
                                data-event={item.kind}
                            >
                                <span className={classNames(styles['eventDate'])}>
                                    {shortDate(item.date)}
                                    <span className={classNames(styles['eventDay'])}>{dayCaption(item.date)}</span>
                                </span>
                                <span
                                    className={classNames(styles['eventKind'])}
                                    style={{ color, background: token.colorFillTertiary }}
                                    title={KIND_LABELS[item.kind]}
                                    aria-label={KIND_LABELS[item.kind]}
                                >
                                    {item.kind === 'loading' ? <IoPushOutline /> : <IoDownloadOutline />}
                                </span>
                                <span className={classNames(styles['eventMain'])} title={item.client ?? undefined}>
                                    {item.client ?? '—'}
                                    <span className={classNames(styles['eventDay'])}>{KIND_LABELS[item.kind]}</span>
                                </span>
                                <span
                                    className={classNames(styles['eventRoute'], styles['eventRouteCol'])}
                                    title={`${item.from ?? '—'} → ${item.to ?? '—'}`}
                                >
                                    {item.from ?? '—'} <IoArrowForward style={{ verticalAlign: -2 }} /> {item.to ?? '—'}
                                </span>
                                <StatusTag label={item.status.title} color={item.status.color ?? undefined} inline />
                            </Link>
                        )
                    })}
                </div>
            ) : (
                <div className={classNames(styles['footnote'])}>
                    На найближчі 7 днів завантажень і розвантажень немає.
                </div>
            )}
        </Panel>
    )
})
